import { Suspense, lazy } from 'react';

import { floorplanFileUrl } from '@/lib/directory/useFloorplan';

/**
 * "○○ 어디 앉아?" 의 답을 <b>도면으로</b> 보여 주는 카드 (DOC-23 §11).
 *
 * <h2>왜 링크가 아니라 카드인가</h2>
 * 처음에는 "도면 열기" 버튼(링크 이벤트)을 생각했다. 그런데 자리를 묻는 사람은 대화창을 떠나고
 * 싶은 것이 아니다 — 층 하나를 듣고 그 자리가 어디쯤인지 <b>보고</b> 싶은 것이고, 그것은 한 화면
 * 안에서 끝나야 하는 일이다. 회의 일정을 달력으로 함께 그리는 것과 같은 판단이다.
 *
 * <h2>답변 문장과 같은 값이다</h2>
 * 문장은 "3층에 있습니다" 이고 카드는 그 3층 도면이다. 카드를 못 그려도 대화는 성립한다 —
 * 그래서 모르는 kind 를 화면이 조용히 넘기는 규칙이 여기에도 그대로 적용된다.
 *
 * <p>pdf.js 는 이 앱에서 가장 무거운 라이브러리라 <b>자리를 물었을 때만</b> 받는다. 대화 화면을
 * 열었다는 이유로 도면 뷰어 값을 물릴 수는 없다(도면 뷰어·직원찾기와 같은 규칙).
 */
const PdfCanvas = lazy(() => import('@/pages/docs/PdfCanvas').then((m) => ({ default: m.PdfCanvas })));

export type FloorplanPayload = {
  /** 찾은 이름. 뷰어가 이 말로 하이라이트를 건다. */
  name: string;
  /** S3 오브젝트 키. 폴더 밖 키는 서버가 거절한다. */
  sheetKey: string;
  sheetName?: string | null;
  page?: number | null;
  /** 도면 글자에서 읽어 낸 층. 못 읽었으면 null 이고, 그때는 파일명을 적는다. */
  floor?: string | null;
};

export function FloorplanCard({ payload }: { payload: FloorplanPayload }) {
  if (!payload.sheetKey) return null;

  const where = payload.floor ?? payload.sheetName ?? '본사 평면도';

  return (
    <div className="card p-3 space-y-2 max-w-[720px]">
      <div className="flex items-center gap-2">
        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
          {payload.name}
        </span>
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{where}</span>
      </div>

      <Suspense
        fallback={
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '40px 0', textAlign: 'center' }}>
            도면을 여는 중…
          </p>
        }
      >
        {/*
          `initialQuery` 로 이름을 넘기면 뷰어가 열리자마자 그 이름을 찾아 하이라이트하고 그 장으로
          넘어간다. `preferFloor` 는 여러 층이 한 파일에 있을 때 서버가 짚은 층부터 보게 한다.
        */}
        <PdfCanvas
          src={floorplanFileUrl(payload.sheetKey)}
          name={payload.sheetName ?? '본사 평면도'}
          height={360}
          initialQuery={payload.name}
          preferFloor={payload.floor ?? undefined}
        />
      </Suspense>
    </div>
  );
}
